import { MutableRefObject, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { setIsProjectSaved, useAppContext } from '../../../context/app-context';
import { useInitializationDataContext } from '../../../context/data-context/data-context';
import { setInitializationData, updateAddresses } from '../../../context/data-context/data-context-actions';
import { CardFace } from '../../../global-types/card-face';
import { InitializationData } from '../../../global-types/initialization-data';
import useAddressSelection from '../../../hooks/use-address-selection';
import { useIsOneToMany } from '../../../hooks/use-is-one-to-many';
import { useSaveProject } from '../../../hooks/use-save-project';
import { useSystemErrorHandling } from '../../../hooks/use-system-error-handling';
import { getRegionalConfig } from '../../../regional-config/get-regional-config';
import { loadProject } from '../../../services/customization';
import { mapAddressesByType } from '../../../utils/address/map-address-by-type';
import { formatResponseData, validateImages } from '../../../utils/utility';
import { mapContactToAddress } from '../utils/map-contact-to-address';
import { useLoadFontFaces } from './use-load-font-faces';

export const useLoadExistingProject = () => {
  const { onSystemError } = useSystemErrorHandling();
  const loadFontFaces = useLoadFontFaces();
  const { saveDraftName } = useSaveProject();
  const {
    saveProjects: { isSavedProjectsEnabled },
  } = getRegionalConfig();
  const { t } = useTranslation();
  const { initializationDataDispatch } = useInitializationDataContext();
  const { appDispatch } = useAppContext();
  const { contacts } = useAddressSelection();
  const isOneToMany = useIsOneToMany();

  const loadExistingProject: (projectID: string, idDataLoaded: MutableRefObject<boolean>) => Promise<void> =
    useCallback(async (projectId, isDataLoaded) => {
      const response = await loadProject(projectId);
      if (!response.data) return;
      // corrects the wrong information we've sent the backend on digital greetings cards below
      const formatedSkuData = formatResponseData(response.data);

      const faces = formatedSkuData?.variables?.template_data?.Faces as CardFace[];
      // confirm all card faces have valid background images
      const missingBackgroundImageError = t('editorView.missingBackgroundImageError');
      validateImages(faces, onSystemError, missingBackgroundImageError);

      await loadFontFaces(formatedSkuData);

      if (isSavedProjectsEnabled) {
        await saveDraftName(formatedSkuData.name, true);
        formatedSkuData.is_draft = true;
      }

      setInitializationData(initializationDataDispatch, formatedSkuData as InitializationData);
      isDataLoaded.current = true;
      setIsProjectSaved(appDispatch, true);

      // If the project is being loaded with contacts already popuplated , we can just load the current contacts
      // This happens when the user is in the address view and clicks on edit project

      if (contacts.length > 0 && isOneToMany) {
        const address = mapContactToAddress(contacts[0]);
        updateAddresses(initializationDataDispatch, { recipient: address });
        return;
      }

      if (formatedSkuData?.addresses) {
        const addresses = mapAddressesByType(formatedSkuData.addresses);
        updateAddresses(initializationDataDispatch, addresses);
      }
    }, []);

  return { loadExistingProject };
};
